import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';

const REIFY_LOGO = 'https://customer-assets.emergentagent.com/job_dbd68d1b-28e6-42d0-902a-82a09fb5a2e9/artifacts/65npxqdg_atdzxq91_ChatGPT%20Image%20Mar%2018%2C%202026%2C%2008_54_02%20AM.png';

const STEPS = [
  { id: 'company', label: 'Company Profile' },
  { id: 'employee', label: 'First Employee' },
  { id: 'job', label: 'First Job' },
  { id: 'done', label: 'All Set' },
];

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8" data-testid="onboarding-step-indicator">
      {STEPS.map((step, i) => {
        const isActive = i === currentStep;
        const isCompleted = i < currentStep;
        return (
          <React.Fragment key={step.id}>
            {i > 0 && (
              <div className={`h-px w-8 sm:w-12 ${isCompleted ? 'bg-cyan-500' : 'bg-gray-700'}`} />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors
                  ${isActive ? 'bg-cyan-600 text-white ring-2 ring-cyan-400/40' : ''}
                  ${isCompleted ? 'bg-cyan-600 text-white' : ''}
                  ${!isActive && !isCompleted ? 'bg-gray-800 text-gray-500 border border-gray-700' : ''}
                `}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                ) : (
                  i + 1
                )}
              </div>
              <span className={`text-[10px] hidden sm:block ${isActive ? 'text-cyan-400' : 'text-gray-600'}`}>
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function CompanyStep({ companyName, form, setForm, onNext }) {
  const [uploading, setUploading] = useState(false);
  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml', 'image/gif'];
    if (!allowed.includes(file.type)) return;
    if (file.size > 5 * 1024 * 1024) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/company/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update('logo_url', res.data.logo_url);
    } catch {
      // silently fail in wizard
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="onboarding-step-company">
      <h2 className="text-xl font-bold text-white mb-1">Set up your company profile</h2>
      <p className="text-gray-500 text-sm mb-6">Add your company details so your team recognizes the platform.</p>
      <div className="space-y-4">
        <div>
          <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Company Name</label>
          <input
            type="text" value={companyName} disabled
            className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-gray-400 cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Company Logo</label>
          <div className="flex items-center gap-3 mb-2">
            <label
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                uploading ? 'bg-gray-800 text-gray-500' : 'bg-cyan-600 hover:bg-cyan-500 text-white'
              }`}
              data-testid="onboarding-upload-logo-btn"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Upload Image
                </>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/gif"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="hidden"
                data-testid="onboarding-logo-file-input"
              />
            </label>
            <span className="text-xs text-gray-600">PNG, JPEG, WebP, SVG, GIF</span>
          </div>
          <div className="relative my-3">
            <div className="absolute inset-x-0 top-1/2 border-t border-gray-800" />
            <div className="relative flex justify-center"><span className="bg-gray-900 px-3 text-xs text-gray-600">or enter a URL</span></div>
          </div>
          <input
            type="url" value={form.logo_url} onChange={e => update('logo_url', e.target.value)}
            placeholder="https://example.com/logo.png"
            className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            data-testid="onboarding-logo-url"
          />
          {form.logo_url && (
            <div className="mt-2 flex items-center gap-2">
              <img src={form.logo_url} alt="Preview" className="h-10 object-contain rounded bg-gray-800 p-1" onError={e => { e.target.style.display = 'none'; }} />
              <span className="text-xs text-gray-500">Preview</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Address</label>
            <input
              type="text" value={form.address} onChange={e => update('address', e.target.value)}
              placeholder="123 Main St, City"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-address"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Phone</label>
            <input
              type="tel" value={form.phone} onChange={e => update('phone', e.target.value)}
              placeholder="(555) 000-0000"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-phone"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Email</label>
            <input
              type="email" value={form.email} onChange={e => update('email', e.target.value)}
              placeholder="info@company.com"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-email"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Website</label>
            <input
              type="url" value={form.website} onChange={e => update('website', e.target.value)}
              placeholder="https://company.com"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-website"
            />
          </div>
        </div>
      </div>
      <div className="mt-8 flex justify-end">
        <button
          onClick={onNext}
          className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-semibold transition-colors"
          data-testid="onboarding-next-company"
        >
          Save & Continue
        </button>
      </div>
    </div>
  );
}

function EmployeeStep({ form, setForm, onNext, onSkip }) {
  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const hasData = form.first_name.trim() || form.last_name.trim();

  return (
    <div data-testid="onboarding-step-employee">
      <h2 className="text-xl font-bold text-white mb-1">Add your first employee</h2>
      <p className="text-gray-500 text-sm mb-6">Add a team member so you can start assigning jobs. You can skip this and add employees later.</p>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">First Name</label>
            <input
              type="text" value={form.first_name} onChange={e => update('first_name', e.target.value)}
              placeholder="John"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-emp-first-name"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Last Name</label>
            <input
              type="text" value={form.last_name} onChange={e => update('last_name', e.target.value)}
              placeholder="Smith"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-emp-last-name"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Position</label>
            <input
              type="text" value={form.position} onChange={e => update('position', e.target.value)}
              placeholder="e.g. Foreman, Technician"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-emp-position"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Department</label>
            <select
              value={form.department} onChange={e => update('department', e.target.value)}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-emp-department"
            >
              <option value="">Select...</option>
              <option value="Operations">Operations</option>
              <option value="Sales">Sales</option>
              <option value="Engineering">Engineering</option>
              <option value="Management">Management</option>
              <option value="Customer Care">Customer Care</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Email</label>
            <input
              type="email" value={form.emp_email} onChange={e => update('emp_email', e.target.value)}
              placeholder="john@company.com"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-emp-email"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Phone</label>
            <input
              type="tel" value={form.emp_phone} onChange={e => update('emp_phone', e.target.value)}
              placeholder="(555) 000-0000"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-emp-phone"
            />
          </div>
        </div>
      </div>
      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={onSkip}
          className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
          data-testid="onboarding-skip-employee"
        >
          Skip for now
        </button>
        <button
          onClick={onNext}
          disabled={!hasData}
          className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-semibold transition-colors"
          data-testid="onboarding-next-employee"
        >
          Add Employee & Continue
        </button>
      </div>
    </div>
  );
}

function JobStep({ form, setForm, onNext, onSkip }) {
  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const hasData = form.customer_name.trim() && form.job_location.trim();

  return (
    <div data-testid="onboarding-step-job">
      <h2 className="text-xl font-bold text-white mb-1">Create your first job</h2>
      <p className="text-gray-500 text-sm mb-6">Set up a job to get your operations rolling. You can skip this and create jobs later.</p>
      <div className="space-y-4">
        <div>
          <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Customer Name *</label>
          <input
            type="text" value={form.customer_name} onChange={e => update('customer_name', e.target.value)}
            placeholder="Acme Corporation"
            className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            data-testid="onboarding-job-customer"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Job Location *</label>
          <input
            type="text" value={form.job_location} onChange={e => update('job_location', e.target.value)}
            placeholder="123 Main St, City"
            className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            data-testid="onboarding-job-location"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Contact Email</label>
            <input
              type="email" value={form.contact_email} onChange={e => update('contact_email', e.target.value)}
              placeholder="client@example.com"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-job-email"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Contact Phone</label>
            <input
              type="tel" value={form.contact_phone} onChange={e => update('contact_phone', e.target.value)}
              placeholder="(555) 000-0000"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="onboarding-job-phone"
            />
          </div>
        </div>
        <div>
          <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Job Details</label>
          <textarea
            value={form.job_details} onChange={e => update('job_details', e.target.value)}
            rows={3}
            placeholder="Brief description of the work to be done..."
            className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none"
            data-testid="onboarding-job-details"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Division</label>
          <select
            value={form.division} onChange={e => update('division', e.target.value)}
            className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            data-testid="onboarding-job-division"
          >
            <option value="">Select...</option>
            <option value="Foundations">Foundations</option>
            <option value="Waterproofing">Waterproofing</option>
            <option value="Septics">Septics</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={onSkip}
          className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
          data-testid="onboarding-skip-job"
        >
          Skip for now
        </button>
        <button
          onClick={onNext}
          disabled={!hasData}
          className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-semibold transition-colors"
          data-testid="onboarding-next-job"
        >
          Create Job & Finish
        </button>
      </div>
    </div>
  );
}

function DoneStep({ onFinish }) {
  return (
    <div className="text-center py-6" data-testid="onboarding-step-done">
      <div className="w-16 h-16 bg-cyan-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">You're all set!</h2>
      <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
        Your company is ready to go. You can always update your settings later from the Company Settings page.
      </p>
      <button
        onClick={onFinish}
        className="px-8 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-bold transition-colors"
        data-testid="onboarding-go-to-dashboard"
      >
        Go to Dashboard
      </button>
    </div>
  );
}

export default function OnboardingWizard({ currentUser, onComplete, onLogout }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [companyForm, setCompanyForm] = useState({
    logo_url: currentUser?.company?.logo_url || '',
    address: currentUser?.company?.address || '',
    phone: currentUser?.company?.phone || '',
    email: currentUser?.company?.email || '',
    website: currentUser?.company?.website || '',
  });

  const [employeeForm, setEmployeeForm] = useState({
    first_name: '', last_name: '', position: '', department: '',
    emp_email: '', emp_phone: '',
  });

  const [jobForm, setJobForm] = useState({
    customer_name: '', job_location: '', contact_email: '', contact_phone: '',
    job_details: '', division: '',
  });

  const handleCompanyNext = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {};
      for (const [k, v] of Object.entries(companyForm)) {
        if (v.trim()) payload[k] = v.trim();
      }
      if (Object.keys(payload).length > 0) {
        await axios.put(`${API}/company/settings`, payload);
      }
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const handleEmployeeNext = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/employees`, {
        first_name: employeeForm.first_name.trim(),
        last_name: employeeForm.last_name.trim(),
        position: employeeForm.position.trim() || 'General',
        department: employeeForm.department || 'Operations',
        email: employeeForm.emp_email.trim() || undefined,
        phone: employeeForm.emp_phone.trim() || undefined,
      });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create employee');
    } finally {
      setSaving(false);
    }
  };

  const handleJobNext = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/jobs`, {
        customer_name: jobForm.customer_name.trim(),
        job_location: jobForm.job_location.trim(),
        contact_email: jobForm.contact_email.trim() || '',
        contact_phone: jobForm.contact_phone.trim() || '',
        job_details: jobForm.job_details.trim() || 'No details provided',
        division: jobForm.division || '',
      });
      await completeOnboarding();
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/company/complete-onboarding`);
    } catch (err) {
      console.error('Failed to mark onboarding as complete:', err);
    }
  };

  const handleSkipEmployee = () => setStep(2);
  const handleSkipJob = async () => {
    await completeOnboarding();
    setStep(3);
  };

  const handleFinish = () => {
    onComplete();
  };

  const companyName = currentUser?.company?.name || 'Your Company';

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-6">
          <img src={REIFY_LOGO} alt="Reify Systems" className="h-10 mx-auto mb-3 object-contain" />
          <p className="text-gray-500 text-xs uppercase tracking-widest">Setup Wizard</p>
        </div>

        <StepIndicator currentStep={step} />

        {/* Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl p-6 sm:p-8">
          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="onboarding-error">
              {error}
            </div>
          )}

          {saving && (
            <div className="mb-4 flex items-center gap-2 text-cyan-400 text-sm">
              <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
              Saving...
            </div>
          )}

          {step === 0 && (
            <CompanyStep companyName={companyName} form={companyForm} setForm={setCompanyForm} onNext={handleCompanyNext} />
          )}
          {step === 1 && (
            <EmployeeStep form={employeeForm} setForm={setEmployeeForm} onNext={handleEmployeeNext} onSkip={handleSkipEmployee} />
          )}
          {step === 2 && (
            <JobStep form={jobForm} setForm={setJobForm} onNext={handleJobNext} onSkip={handleSkipJob} />
          )}
          {step === 3 && (
            <DoneStep onFinish={handleFinish} />
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-4">
          <button onClick={onLogout} className="text-gray-600 hover:text-gray-400 text-xs" data-testid="onboarding-logout">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
